const slugifyPart = (value = '') =>
  String(value)
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9\s-]/g, '')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '') || 'item';

export const isNumericListingRef = (value) => /^\d+$/.test(String(value || '').trim());

export const isMarketplaceItemCodeRef = (value) => {
  const raw = String(value || '').trim().toUpperCase();
  return /^ITM[A-Z0-9]+$/.test(raw);
};

export const getItemPublicPath = (item) => {
  const categorySlug = slugifyPart(item?.category || 'items');
  const itemSlug = slugifyPart(item?.title || item?.name || 'item');
  return `/pre-owned-items/${categorySlug}/${itemSlug}`;
};

/** Stable detail URL — numeric id works for luxury/defected items excluded from default browse. */
export const getMarketplaceItemDetailPath = (item) => {
  const code = item?.item_code;
  if (code) {
    return `/pre-owned-items/${encodeURIComponent(String(code).toUpperCase())}`;
  }
  return getItemPublicPath(item);
};

/**
 * Owner edit URL — wedding (and later luxury) listings must not open the
 * generic pre-owned edit form after an admin/category move.
 */
export const getEditListingPath = (item) => {
  const itemRef = item?.item_code || item?.id;
  if (itemRef == null || itemRef === '') return '/my-catalogue';
  const encoded = encodeURIComponent(String(itemRef));
  const listingType = String(item?.listing_type || '').toLowerCase();
  const category = String(item?.category || '');
  const weddingCategories = new Set([
    'Bridal Gowns and Essentials',
    'Bridesmaids / soiree dresses',
    'Bridesmaids Dresses',
    'Bridal Veil',
    'Bridal Accessories',
    'Bridal Shoes',
    'Groom Suits & Essentials',
    'Grooms Accessories',
    'Men Formal shoes',
    'Men Shoes',
    'Suits & Prom Wear',
    'Event Planning Services',
    'Event Decorations',
  ]);
  if (listingType === 'wedding_essentials' || weddingCategories.has(category)) {
    return `/edit-wedding-pre-loved-item/${encoded}`;
  }
  return `/edit-item/${encoded}`;
};

export const getDesignerItemPublicPath = (item) => {
  const code = item?.item_code || item?.id;
  if (code != null && code !== '') return `/designer-items/${encodeURIComponent(code)}`;
  return '/designers';
};

export const getBoutiqueItemPublicPath = (item) => {
  const code = item?.item_code || item?.id;
  if (code != null && code !== '') return `/boutique-items/${encodeURIComponent(code)}`;
  return '/boutiques';
};

export const getArtistItemPublicPath = (item) => {
  const code = item?.item_code || item?.id;
  if (code != null && code !== '') return `/artist-items/${encodeURIComponent(code)}`;
  return '/artists';
};

export const getListingPublicPath = (item, listingKind) => {
  if (listingKind === 'designer_item' || listingKind === 'designer') {
    return getDesignerItemPublicPath(item);
  }
  if (listingKind === 'boutique_item' || listingKind === 'boutique') {
    return getBoutiqueItemPublicPath(item);
  }
  if (listingKind === 'artist_item' || listingKind === 'artist') {
    return getArtistItemPublicPath(item);
  }
  return getMarketplaceItemDetailPath(item);
};

/** Optional short alias — keep share URLs on hub paths until older app builds are gone. */
export const canonicalPathForListingRef = (itemRef) => {
  const code = String(itemRef || '').trim().toUpperCase();
  if (!code) return '/pre-owned-items';
  if (code.startsWith('DSN')) return `/designer-items/${encodeURIComponent(code)}`;
  if (code.startsWith('BTQ')) return `/boutique-items/${encodeURIComponent(code)}`;
  if (code.startsWith('CTG')) return `/artist-items/${encodeURIComponent(code)}`;
  return `/pre-owned-items/${encodeURIComponent(code)}`;
};

/** Detail path for any listing kind (wedding hub, luxury, etc.). */
export const getListingDetailPath = (item, listingKind) => getListingPublicPath(item, listingKind);

/** Router state so detail pages can load by id when slug browse lists omit the item. */
export const getListingLinkState = (item, listingKind) => {
  const kind = listingKind || item?.listingKind;
  if (!item?.id && item?.id !== 0) return undefined;
  if (kind === 'designer_item' || kind === 'designer') return { designerItemId: item.id };
  if (kind === 'boutique_item' || kind === 'boutique') return { boutiqueItemId: item.id };
  return { itemId: item.id };
};

export const isMatchingItemSlug = (item, categorySlug, itemSlug) => {
  if (!item || !categorySlug || !itemSlug) return false;
  return (
    slugifyPart(item.category || 'items') === slugifyPart(categorySlug) &&
    slugifyPart(item.title || item.name || 'item') === slugifyPart(itemSlug)
  );
};
